// src/services/dataApiBusy.js
// Under heavy load the Data API (data.tablemate.net) turns heavy charts away with
// HTTP 503, a Retry-After header and {"error": "busy", "detail": "..."}.
// redir.php forwards the status and body unchanged.
import { useCallback, useEffect, useRef, useState } from "react";

export const BUSY_MESSAGE = "Heavy server load, try again in a few moments.";
const DEFAULT_RETRY_SECONDS = 5;

export class DataApiBusyError extends Error {
  constructor(message, retryAfterSeconds) {
    super(message);
    this.name = "DataApiBusyError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Returns a DataApiBusyError for a 503 response, otherwise null.
 * Reads the body, so call it before reading the response yourself.
 */
export async function busyErrorFrom(response) {
  if (!response || response.status !== 503) return null;

  let detail = "";
  try {
    const body = JSON.parse(await response.text());
    if (typeof body?.detail === "string") detail = body.detail.trim();
  } catch (_) {
    // Not JSON (e.g. a proxy error page): fall back to the default message
  }

  // Retry-After isn't always readable (redir.php may drop it, CORS hides it)
  const seconds = Number.parseInt(response.headers.get("Retry-After"), 10);
  const retryAfter =
    Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 60) : DEFAULT_RETRY_SECONDS;

  return new DataApiBusyError(detail || BUSY_MESSAGE, retryAfter);
}

/**
 * Busy state for one Data API request. Put `attempt` in the fetch effect's
 * dependencies; `retry()` re-runs it. After the first busy answer for a given
 * `requestKey` it retries once by itself after Retry-After seconds.
 */
export function useDataApiBusy(requestKey) {
  const [busy, setBusy] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const autoRetried = useRef(false);

  useEffect(() => {
    autoRetried.current = false;
    setBusy(null);
  }, [requestKey]);

  useEffect(() => {
    if (!busy || autoRetried.current) return undefined;
    autoRetried.current = true;
    const id = window.setTimeout(
      () => setAttempt((a) => a + 1),
      busy.retryAfterSeconds * 1000
    );
    return () => window.clearTimeout(id);
  }, [busy]);

  const retry = useCallback(() => {
    setBusy(null);
    setAttempt((a) => a + 1);
  }, []);

  return { busy, setBusy, attempt, retry };
}
